import { FC, useMemo } from 'react';
import type { SopMes, SopResumoLinhaMes, SopTotaisLinha, TipoLinhaSop } from '@/types/dashboards';
import { baixarArquivo } from '@/utils/arquivo';
import { produzidoBase } from '@/utils/equiplexSop';
import {
  colunasDasLinhas,
  csvPorLinha,
  linhaDoMes,
  totalDoPeriodoPorLinha,
  type ColunaLinha,
  type TotalLinha,
} from '@/utils/equiplexSopLinhas';
import { fmtDesvio, fmtPct, fmtUn } from '@/utils/painelFormat';
import PainelCard from '../PainelCard';

interface Props {
  /** Só os meses do período (já recortados). */
  resumos: SopResumoLinhaMes[];
  meses: SopMes[];
}

const th = 'py-2 pr-3 text-right font-medium';
/** O Green Pack fica separado das linhas da fábrica por um traço (não soma nela). */
const separador = (tipo: TipoLinhaSop): string => (tipo === 'sopro' ? 'border-l border-theme-border pl-3' : '');
/** Linha de produto mostra a fatia da fábrica; fábrica e Green Pack mostram o programado. */
const temParticipacao = (tipo: TipoLinhaSop): boolean => tipo === 'linha';

/** Produzido na régua do desvio; no mês aberto, o total do mês embaixo (como no resumo por mês). */
const CelulaProduzido: FC<{ l: SopTotaisLinha | undefined; emAndamento: boolean }> = ({ l, emAndamento }) => {
  if (!l) return <>—</>;
  return (
    <>
      {fmtUn(produzidoBase(l))}
      {emAndamento && (
        <div className="whitespace-nowrap text-xs text-content-muted" title="Produzido de todas as OPs do mês até agora">
          de {fmtUn(l.produzido)}
        </div>
      )}
    </>
  );
};

/** A segunda coluna do grupo: % da fábrica (linha de produto) ou programado (fábrica e Green Pack). */
const segundaColuna = (tipo: TipoLinhaSop, l: SopTotaisLinha | undefined): string => {
  if (temParticipacao(tipo)) return fmtPct(l?.participacao_pct ?? null);
  return l ? fmtUn(l.programado_base) : '—';
};

const CelulasDoMes: FC<{ r: SopResumoLinhaMes; coluna: ColunaLinha }> = ({ r, coluna }) => {
  const l = linhaDoMes(r, coluna.nome);
  const segunda = segundaColuna(coluna.tipo, l);
  return (
    <>
      <td className={`py-2 pr-3 text-right font-medium ${separador(coluna.tipo)}`}>
        <CelulaProduzido l={l} emAndamento={r.em_andamento} />
      </td>
      <td className="py-2 pr-3 text-right">{segunda}</td>
      <td className="py-2 pr-3 text-right">{fmtDesvio(l?.desvio_pct ?? null)}</td>
    </>
  );
};

const CelulasDoTotal: FC<{ t: TotalLinha }> = ({ t }) => (
  <>
    <td className={`py-2 pr-3 text-right ${separador(t.tipo)}`}>{fmtUn(t.produzidoBase)}</td>
    <td className="py-2 pr-3 text-right">{temParticipacao(t.tipo) ? fmtPct(t.participacaoPct) : fmtUn(t.programadoBase)}</td>
    <td className="py-2 pr-3 text-right">{fmtDesvio(t.desvioPct)}</td>
  </>
);

const TituloColuna: FC<{ coluna: ColunaLinha }> = ({ coluna }) => (
  <th colSpan={3} className={`pt-2 pr-3 text-center font-semibold text-content-secondary normal-case text-xs ${separador(coluna.tipo)}`}>
    {coluna.nome}
    {coluna.tipo === 'sopro' && (
      <span className="block text-2xs font-normal text-content-muted" title="O frasco soprado vira produto acabado de grande volume: somar contaria duas vezes">
        sopro, fora da fábrica
      </span>
    )}
  </th>
);

const FragmentoCabecalho: FC<{ coluna: ColunaLinha }> = ({ coluna }) => (
  <>
    <th className={`${th} ${separador(coluna.tipo)}`}>Produzido</th>
    {temParticipacao(coluna.tipo) ? (
      <th className={th} title="Fatia do produzido da fábrica no mês">
        % da fábrica
      </th>
    ) : (
      <th className={th}>Programado</th>
    )}
    <th className={th}>Desvio</th>
  </>
);

const BotaoCsv: FC<{ onCsv: () => void }> = ({ onCsv }) => (
  <button
    type="button"
    onClick={onCsv}
    data-testid="sop-por-linha-csv"
    className="inline-flex items-center gap-1 rounded-lg border border-theme-border px-3 py-1.5 text-xs text-content-secondary hover:bg-surface-hover"
  >
    <span aria-hidden className="material-symbols-outlined text-[16px] leading-none">
      download
    </span>
    Baixar CSV
  </button>
);

/**
 * Volume produzido por linha, mês a mês (pedido do Attila, 20/10/2026): o número que a diretoria
 * pede todo mês. Fábrica = o produto acabado (grande + pequeno volume); a linha vem da família do
 * item e, nos lotes piloto (família 150), da máquina. Green Pack = o sopro, à parte. Mostra o
 * período e os meses de antes dele, até 6 (27/10/2026: o padrão virou o mês atual).
 */
const ProducaoPorLinhaTable: FC<Props> = ({ resumos, meses }) => {
  const colunas = useMemo(() => colunasDasLinhas(resumos), [resumos]);
  const totais = useMemo(() => totalDoPeriodoPorLinha(resumos), [resumos]);
  const periodos = useMemo(() => Object.fromEntries(meses.map((m) => [m.key, m.range])), [meses]);

  if (!resumos.length) return null;

  const baixarCsv = () =>
    baixarArquivo(new Blob([csvPorLinha(resumos, periodos)], { type: 'text/csv;charset=utf-8' }), 'producao-por-linha.csv');

  return (
    <PainelCard
      testId="sop-por-linha"
      titulo="Produzido por linha"
      subtitulo="Produzido das OPs do mês por linha, com o desvio contra o programado. Fábrica = produto acabado (grande + pequeno volume); a linha vem da família do item e, nos lotes piloto, da máquina. Green Pack = o sopro (frascos), fora da fábrica. No mês em andamento, só as OPs dos dias fechados; o total do mês fica embaixo."
      acoes={<BotaoCsv onCsv={baixarCsv} />}
    >
      <div className="overflow-x-auto -mx-5 px-5">
        <table className="w-full min-w-[1100px] text-sm">
          <thead>
            <tr className="text-left text-2xs uppercase tracking-wide text-content-muted">
              <th rowSpan={2} className="py-2 pr-3 align-bottom font-medium border-b border-theme-border">
                Mês
              </th>
              {colunas.map((c) => (
                <TituloColuna key={c.nome} coluna={c} />
              ))}
            </tr>
            <tr className="border-b border-theme-border text-2xs uppercase tracking-wide text-content-muted">
              {colunas.map((c) => (
                <FragmentoCabecalho key={c.nome} coluna={c} />
              ))}
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {resumos.map((r) => (
              <tr key={r.mes} className="border-b border-theme-border" data-testid={`por-linha-${r.mes}`}>
                <td className="py-2 pr-3 whitespace-nowrap">
                  <div className="font-medium text-content-primary">
                    {r.label}
                    {r.em_andamento && <span className="ml-1 text-2xs text-content-muted">(em andamento)</span>}
                  </div>
                  <div className="text-xs text-content-muted">{periodos[r.mes]}</div>
                </td>
                {colunas.map((c) => (
                  <CelulasDoMes key={c.nome} r={r} coluna={c} />
                ))}
              </tr>
            ))}
          </tbody>
          {resumos.length > 1 && (
            <tfoot className="tabular-nums font-semibold text-content-primary" data-testid="por-linha-total">
              <tr>
                <td className="py-2 pr-3">Total dos {resumos.length} meses</td>
                {totais.map((t) => (
                  <CelulasDoTotal key={t.linha} t={t} />
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </PainelCard>
  );
};

export default ProducaoPorLinhaTable;
